import { kpiTypes } from 'helpers/constants';
import { translate } from 'helpers/translateHelper';
import { CHART_MAX_KPI_LIMIT, opButtons } from './constants';
import { TimeSeriesApi, AnalyticsApi } from 'api/';
import { Trend } from './Graph/Trend';

import { clone, map, findIndex, filter } from 'lodash';
import { createParamMeasurementAssetObjTrend, createParamMeasurementAssetObjV2 } from '../../helpers';
import { setColor } from '../../DetailedInformation/commonTrendHelpers';
import { HistogramV2 } from './Graph/Histogram V2';
import useUserCapability from 'sharedHooks/useUserCapability';
import { ScatterPlotV2 } from './Graph/ScatterPlot V2';

const getTabsVisibility = () => {
    const userCapabilities = useUserCapability();
    // User capabilities for microservice APIs only
    return {
        [opButtons.histogramV2]: userCapabilities.hasHistogramV2UserCapability(),
        [opButtons.scatterPlotV2]: userCapabilities.hasScatterV2UserCapability(),
    };
};

export const transformPlotRequestDataV2 = (component, series, isFromScatter) => {
    return map(series, (item, index) => {
        return {
            assetId: component?.[index]?.id,
            ...isFromScatter ? { scatter: item?.scatterKey } : { histogram: item?.histogramKey }
        };
    });
};

export const transformPlotRequestDataTrend = (list) => {
    const data = list.map((val) => val?.componentKPIType[0]);
    return data.map((l) => {
        return {
            assetId: l.assetId,
            timeseries: { timeseriesKey: l?.timeseries?.timeseriesKey }
        };
    });
};

export const getToggleOptions = () => {
    const allTabs = [{
        label: translate('ABB.Powertrain.Frontend.crossAssetAnalysisTrendSignalsBtn'),
        value: opButtons.trend,
        component: Trend
    },
    {
        label: translate('ABB.Powertrain.Frontend.opScatterPlotTabBtn'),
        value: opButtons.scatterPlotV2,
        component: ScatterPlotV2
    },
    {
        label: translate('ABB.Powertrain.Frontend.opHistogramTabBtn'),
        value: opButtons.histogramV2,
        component: HistogramV2,
    }];

    const tabVisibility = getTabsVisibility();

    return filter(allTabs, (tab) => {
        if (tab.value === opButtons.histogramV2) {
            return tabVisibility[tab.value];
        } else if (tab.value === opButtons.scatterPlotV2) {
            return tabVisibility[tab.value];
        }
        return true;
    });

};

const checkCompatibility = (selected, current) => {
    if (selected.kpi.measurementTypeIdentifier !== current.kpi.measurementTypeIdentifier) {
        return false;
    }
    const checkProperties = item => item && item.data && item.data.length && item.data[0] && item.data[0].config;
    if (!checkProperties(selected) || !checkProperties(current)) {
        return true;
    }
    return selected.data[0].config.xUnitGroupID === current.data[0].config.xUnitGroupID
        && selected.data[0].config.yUnitGroupID === current.data[0].config.yUnitGroupID;
};

const checkCompatibilityV2 = (selected, current, isFromScatter) => {
    if (selected?.kpi?.nameTextKey || current?.kpi?.nameTextKey) {
        if (selected?.kpi?.nameTextKey !== current?.kpi?.nameTextKey) {
            return false;
        }
    } else if (isFromScatter && selected?.kpi?.scatterKey?.toLowerCase() !== current?.kpi?.scatterKey?.toLowerCase()) {
        return false;
    } else if (!isFromScatter && selected?.kpi?.histogramKey?.toLowerCase() !== current?.kpi?.histogramKey?.toLowerCase()) {
        return false;
    }
    const checkProperties = item => item && item.data && item.data.length && item.data[0] && item.data[0].config;
    if (!checkProperties(selected) || !checkProperties(current)) {
        return true;
    }
    return selected.data[0].config.xUnitGroupId === current.data[0].config.xUnitGroupId
        && selected.data[0].config.yUnitGroupId === current.data[0].config.yUnitGroupId;
};

export const getUpdatedChartData = (state, action) => {
    let chartData = state.chartData && state.chartData.length > 0 ? clone(state.chartData) : [];
    const activeTab = state.activeTab;
    const getCheckCompatibility = () => [opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab)
        ? checkCompatibilityV2(action, state.chartData[0], activeTab === opButtons.scatterPlotV2)
        : checkCompatibility(action, state.chartData[0]);
    if (chartData.length > 0 && action.kpiType !== kpiTypes.TREND
        && !getCheckCompatibility()) {
        chartData = [];
    }

    const chartDataItem = {
        data: action.data,
        component: action.component,
        powertrain: state.selectedPowertrain,
        kpi: action.kpi
    };

    const index = action.kpiType === kpiTypes.TREND ?
        findIndex(chartData, (item) => item?.kpi?.timeseriesKey === chartDataItem?.kpi?.timeseriesKey && item?.component?.id === chartDataItem?.component?.id) :
        activeTab === opButtons.histogramV2 ? findIndex(chartData, (item) => item?.kpi?.histogramKey === chartDataItem?.kpi?.histogramKey && item?.component?.id === chartDataItem?.component?.id)
            : activeTab === opButtons.scatterPlotV2 ? findIndex(chartData, (item) => item?.kpi?.scatterKey === chartDataItem?.kpi?.scatterKey && item?.component?.id === chartDataItem?.component?.id)
                : findIndex(chartData, (item) => item?.kpi?.measurementTypeIdentifier === chartDataItem?.kpi?.measurementTypeIdentifier && item?.component?.id === chartDataItem?.component?.id);

    if (index >= 0) {
        chartData.splice(index, 1, { ...chartDataItem, color: chartData[index].color });
    } else {
        chartData.push(chartDataItem);
        if (chartData.length > CHART_MAX_KPI_LIMIT) {
            chartData.shift();
        }
    }

    setColor(chartData);
    return chartData;
};

export const getKpiTypeByActiveTab = (tabName) => {
    switch (tabName) {
        case opButtons.trend: {
            return kpiTypes.TREND;
        }
        case opButtons.scatterPlotV2: {
            return kpiTypes.SCATTER;
        }
        case opButtons.histogramV2: {
            return kpiTypes.HISTOGRAM;
        }
        default:
            return;
    }
};


export const paramListForChartDataRequest = (chartData, selectedKPIType, activeTab) => {
    return chartData ? map(chartData, (item) => {
        return {
            componentKPIType: selectedKPIType === 1 ? createParamMeasurementAssetObjTrend(item.kpi, item.component)
                : createParamMeasurementAssetObjV2(item.kpi, item.component, activeTab === opButtons.scatterPlotV2),
            kpi: item.kpi,
            component: item.component,
            color: item.color
        };
    }) : null;
};

export const getPlotAPI = (state) => {
    const { chartView: { selectedKPIType } } = state;
    switch (selectedKPIType) {
        case kpiTypes.SCATTER:
            return AnalyticsApi.getScatterV2Data;
        case kpiTypes.HISTOGRAM:
            return AnalyticsApi.getHistogramV2Data;
        default:
            return TimeSeriesApi.getTrend;
    }
};
